import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
    Calendar as CalIcon, ChevronLeft, ChevronRight, Clock, Briefcase, User2, MapPin,
    List as ListIcon, LayoutGrid, Video, Send as SendIcon, Plus, Search, Filter, X,
    Bell, StickyNote, Eye, Phone, Mail, MessageCircle, Users as UsersIcon, Sparkles,
    AlignJustify,
} from "lucide-react";
import InterviewDetailDialog from "@/components/InterviewDetailDialog";
import CalendarNoteDialog from "@/components/CalendarNoteDialog";
import CandidateProfileDialog from "@/components/CandidateProfileDialog";
import BulkSlotsDialog from "@/components/BulkSlotsDialog";
import RemindersDialog from "@/components/RemindersDialog";
import SmartInterviewRow from "@/components/SmartInterviewRow";
import ResendInviteIconBtn from "@/components/calendar/ResendInviteIconBtn";
import { Link } from "react-router-dom";

const DAY_MS = 86400000;

function startOfMonth(d) { const x = new Date(d.getFullYear(), d.getMonth(), 1); x.setHours(0,0,0,0); return x; }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function startOfWeek(d) {
    const x = new Date(d);
    const diff = x.getDay();
    x.setDate(x.getDate() - diff);
    x.setHours(0,0,0,0);
    return x;
}
function fmtTime(iso) {
    try { return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
}
function ymd(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function isSameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }

const COLOR_DOT = {
    grey: "bg-slate-400", blue: "bg-ikb", amber: "bg-amber-500",
    rose: "bg-rose-500", emerald: "bg-emerald-500",
};

export default function CalendarPage() {
    const { user } = useAuth();
    const [cursor, setCursor] = useState(startOfMonth(new Date()));
    const [items, setItems] = useState([]);   // interviews
    const [notes, setNotes] = useState([]);
    const [interviewers, setInterviewers] = useState([]);
    const [recruiters, setRecruiters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState("month");
    const [rangeStart, setRangeStart] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 7); return ymd(d); });
    const [rangeEnd, setRangeEnd] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 30); return ymd(d); });
    const [selected, setSelected] = useState(null);
    const [noteSelected, setNoteSelected] = useState(null);
    const [companyAddress, setCompanyAddress] = useState("");
    const [previewCid, setPreviewCid] = useState(null);
    const [showBulk, setShowBulk] = useState(false);
    const [showReminders, setShowReminders] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());   // for side panel + day view
    const [showAll, setShowAll] = useState(true);   // show everyone's interviews
    // Per-recruiter visibility toggle. Map<recruiterId, bool> — all ON by default.
    const [recruiterVisible, setRecruiterVisible] = useState({});

    // Filters
    const [search, setSearch] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [fInterviewer, setFInterviewer] = useState("");
    const [fRecruiter, setFRecruiter] = useState("");
    const [fJob, setFJob] = useState("");
    const [fCandidate, setFCandidate] = useState("");
    const [fMode, setFMode] = useState("");
    const [fStatus, setFStatus] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            let since, until;
            if (viewMode === "list" || viewMode === "schedule") {
                since = rangeStart; until = rangeEnd;
            } else if (viewMode === "day") {
                since = ymd(selectedDate); until = ymd(selectedDate);
            } else {
                const ms = startOfMonth(cursor); const me = endOfMonth(cursor);
                const grid = startOfWeek(ms); const gridEnd = new Date(grid.getTime() + 42 * DAY_MS);
                since = ymd(grid); until = ymd(gridEnd);
            }
            const [{ data: rangeData }, { data: notesData }] = await Promise.all([
                api.get("/calendar/interviews/range", { params: { since, until } }).catch(() => ({ data: { items: [] } })),
                api.get("/calendar/notes", { params: { since, until } }).catch(() => ({ data: { items: [] } })),
            ]);
            setItems(rangeData?.items || []);
            setNotes(notesData?.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [viewMode, rangeStart, rangeEnd, cursor, selectedDate]);

    useEffect(() => {
        api.get("/company").then(({ data }) => {
            const addr = data?.address || data?.location || "";
            if (addr) setCompanyAddress(addr);
        }).catch(() => {});
        api.get("/company/interviewers").then(({ data }) => setInterviewers(data || [])).catch(() => {});
        api.get("/company/recruiters").then(({ data }) => setRecruiters(data || [])).catch(() => {});
    }, []);

    // Filter logic — applied to items
    const isRecruiterOn = (id) => recruiterVisible[id] !== false; // default ON
    const filteredItems = useMemo(() => {
        const q = search.trim().toLowerCase();
        return items.filter((it) => {
            if (!showAll && user?.id && it.created_by !== user.id) return false;
            // Recruiter color-toggle filter (ON by default)
            if (it.created_by && !isRecruiterOn(it.created_by)) return false;
            if (q) {
                const blob = [
                    it.candidate?.name, it.job?.title, it.round_name,
                    (it.interviewer_names || []).join(" "),
                    it.location, it.candidate?.email,
                ].filter(Boolean).join(" ").toLowerCase();
                if (!blob.includes(q)) return false;
            }
            if (fInterviewer && !(it.interviewer_ids || []).includes(fInterviewer)) return false;
            if (fRecruiter && it.created_by !== fRecruiter) return false;
            if (fJob && it.job_id !== fJob) return false;
            if (fCandidate && it.candidate_id !== fCandidate) return false;
            if (fMode) {
                const mode = it.online_offline || (it.mode === "Video" ? "online" : "offline");
                if (mode !== fMode) return false;
            }
            if (fStatus && (it.status || "Scheduled") !== fStatus) return false;
            return true;
        });
    }, [items, search, fInterviewer, fRecruiter, fJob, fCandidate, fMode, fStatus, showAll, user, recruiterVisible]);

    // Recruiter color palette — stable per-id hash for tile color coding
    const colorByRecruiter = useMemo(() => {
        const palette = [
            { bg: "bg-blue-50", border: "border-ikb", dot: "bg-ikb" },
            { bg: "bg-emerald-50", border: "border-emerald-500", dot: "bg-emerald-500" },
            { bg: "bg-rose-50", border: "border-rose-500", dot: "bg-rose-500" },
            { bg: "bg-violet-50", border: "border-violet-500", dot: "bg-violet-500" },
            { bg: "bg-orange-50", border: "border-orange-500", dot: "bg-orange-500" },
            { bg: "bg-teal-50", border: "border-teal-500", dot: "bg-teal-500" },
            { bg: "bg-pink-50", border: "border-pink-500", dot: "bg-pink-500" },
        ];
        const map = new Map();
        // Stable: include all recruiters from /company/recruiters + creators seen in items + note creators
        const idsFromItems = items.map((it) => it.created_by).filter(Boolean);
        const idsFromNotes = notes.map((n) => n.created_by || n.owner_id).filter(Boolean);
        const idsFromRecruiters = (recruiters || []).map((r) => r.id);
        const ids = [...new Set([...idsFromRecruiters, ...idsFromItems, ...idsFromNotes])];
        ids.forEach((id, idx) => map.set(id, palette[idx % palette.length]));
        return map;
    }, [items, notes, recruiters]);
    const colorForRecruiter = (id) => colorByRecruiter.get(id) || { bg: "bg-blue-50", border: "border-ikb", dot: "bg-ikb" };

    // Active recruiters appearing in this calendar view + their display label.
    const recruiterLegend = useMemo(() => {
        const byId = new Map();
        for (const r of (recruiters || [])) byId.set(r.id, { id: r.id, name: r.name || r.email, role: r.role });
        // Add ad-hoc creators (e.g., employer who scheduled directly) that aren't in /recruiters
        for (const it of items) {
            const id = it.created_by; if (!id || byId.has(id)) continue;
            byId.set(id, { id, name: it.created_by_name || "Unknown", role: null });
        }
        for (const n of notes) {
            const id = n.created_by || n.owner_id; if (!id || byId.has(id)) continue;
            byId.set(id, { id, name: n.created_by_name || n.owner_name || "Unknown", role: null });
        }
        return [...byId.values()].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [recruiters, items, notes]);

    const toggleRecruiter = (id) => setRecruiterVisible((m) => ({ ...m, [id]: m[id] === false }));
    const allRecruitersOn = () => setRecruiterVisible({});
    const onlyRecruiter = (id) => {
        const next = {};
        for (const r of recruiterLegend) if (r.id !== id) next[r.id] = false;
        setRecruiterVisible(next);
    };

    const filteredNotes = useMemo(() => {
        const q = search.trim().toLowerCase();
        return notes.filter((n) => {
            const creator = n.created_by || n.owner_id;
            if (creator && !isRecruiterOn(creator)) return false;
            if (q && ![n.title, n.body, n.assigned_to_name, n.created_by_name].filter(Boolean).join(" ").toLowerCase().includes(q)) return false;
            return true;
        });
    }, [notes, search, recruiterVisible]);

    const weeks = useMemo(() => {
        const monthStart = startOfMonth(cursor); const monthEnd = endOfMonth(cursor);
        const gridStart = startOfWeek(monthStart);
        const out = [];
        let day = new Date(gridStart);
        for (let w = 0; w < 6; w++) {
            const week = [];
            for (let d = 0; d < 7; d++) {
                week.push(new Date(day));
                day = new Date(day.getTime() + DAY_MS);
            }
            out.push(week);
            if (day > monthEnd && day.getDay() === 0) break;
        }
        return out;
    }, [cursor]);

    const eventsByDay = useMemo(() => {
        const map = new Map();
        for (const it of filteredItems) {
            if (!it.scheduled_at) continue;
            const dt = new Date(it.scheduled_at);
            const key = `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(it);
        }
        return map;
    }, [filteredItems]);

    const notesByDay = useMemo(() => {
        const map = new Map();
        for (const n of filteredNotes) {
            if (!n.date) continue;
            const [y, m, d] = n.date.split("-").map(Number);
            const key = `${y}-${m - 1}-${d}`;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(n);
        }
        return map;
    }, [filteredNotes]);

    const today = new Date();

    const upcoming = useMemo(() => {
        const now = Date.now();
        return filteredItems
            .filter((i) => i.scheduled_at && new Date(i.scheduled_at).getTime() >= now)
            .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
            .slice(0, 8);
    }, [filteredItems]);

    const jobsForFilter = useMemo(() => {
        const map = new Map();
        for (const it of items) {
            if (it.job_id && it.job?.title) map.set(it.job_id, it.job.title);
        }
        return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
    }, [items]);

    const candidatesForFilter = useMemo(() => {
        const map = new Map();
        for (const it of items) {
            if (it.candidate_id && it.candidate?.name) map.set(it.candidate_id, it.candidate.name);
        }
        return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
    }, [items]);

    const addInterview = (date) => {
        const d = date || new Date();
        const scheduled = new Date(d);
        // Default at 10am unless date already has a time
        if (scheduled.getHours() === 0 && scheduled.getMinutes() === 0) scheduled.setHours(10, 0, 0, 0);
        api.post("/calendar/interviews", {
            round_name: "Interview slot",
            scheduled_at: scheduled.toISOString(),
            duration_minutes: 60,
            online_offline: "online",
            mode: "Video",
        }).then(({ data }) => {
            toast.success("Slot created — assign candidate & job from the dialog.");
            setSelected(data);
            load();
        }).catch((err) => toast.error(formatApiError(err)));
    };

    const onDayClick = (d) => {
        // Single-click selects the date (drives the side panel + Day view).
        // The plus button still creates a placeholder slot via addInterview.
        setSelectedDate(new Date(d));
    };

    const onAddNote = (date) => {
        setNoteSelected({ date: ymd(date || selectedDate || new Date()), color: "blue", title: "", body: "", time: "", reminder_minutes_before: null });
    };

    // Listen for power-menu events
    useEffect(() => {
        const onSlot = () => addInterview(selectedDate || new Date());
        const onNote = () => onAddNote(selectedDate || new Date());
        const onBulk = () => setShowBulk(true);
        window.addEventListener("pw-cal-open-slot", onSlot);
        window.addEventListener("pw-cal-open-note", onNote);
        window.addEventListener("pw-cal-open-bulk", onBulk);
        return () => {
            window.removeEventListener("pw-cal-open-slot", onSlot);
            window.removeEventListener("pw-cal-open-note", onNote);
            window.removeEventListener("pw-cal-open-bulk", onBulk);
        };
        // eslint-disable-next-line
    }, [selectedDate]);

    // If URL says ?openSlot=1 / ?bulkSlots=1 / ?openNote=1, fire once.
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            if (params.get("openSlot")) { addInterview(new Date()); cleanUrl(); }
            else if (params.get("bulkSlots")) { setShowBulk(true); cleanUrl(); }
            else if (params.get("openNote")) { onAddNote(new Date()); cleanUrl(); }
        } catch { /* ignore */ }
        function cleanUrl() {
            try { window.history.replaceState({}, "", window.location.pathname); } catch { /* ignore */ }
        }
        // eslint-disable-next-line
    }, []);

    const clearFilters = () => {
        setSearch(""); setFInterviewer(""); setFRecruiter(""); setFJob(""); setFCandidate(""); setFMode(""); setFStatus("");
    };
    const anyFilter = !!(search || fInterviewer || fRecruiter || fJob || fCandidate || fMode || fStatus);

    return (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-4">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb">Schedule</div>
                    <h1 className="font-display font-black text-2xl sm:text-4xl mt-2 flex items-center gap-3">
                        <CalIcon className="w-8 h-8 text-ikb" /> Calendar
                    </h1>
                    <p className="text-foreground/80 mt-2 text-sm">Interviews, slots and reminders across every recruiter.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="inline-flex border border-border bg-white" data-testid="cal-view-toggle">
                        <button onClick={() => setViewMode("month")} className={`px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 ${viewMode === "month" ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid="cal-view-month"><LayoutGrid className="w-3 h-3" /> Month</button>
                        <button onClick={() => setViewMode("schedule")} className={`px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 border-l border-border ${viewMode === "schedule" ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid="cal-view-schedule"><AlignJustify className="w-3 h-3" /> Schedule</button>
                        <button onClick={() => setViewMode("day")} className={`px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 border-l border-border ${viewMode === "day" ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid="cal-view-day"><Clock className="w-3 h-3" /> Day</button>
                        <button onClick={() => setViewMode("list")} className={`px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 border-l border-border ${viewMode === "list" ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid="cal-view-list"><ListIcon className="w-3 h-3" /> List</button>
                    </div>
                    <button onClick={() => addInterview(selectedDate || new Date())} className="text-xs font-mono uppercase tracking-widest border border-ikb bg-ikb text-white px-3 py-2 hover:bg-[#001f7a] inline-flex items-center gap-1" data-testid="cal-add-interview">
                        <Plus className="w-3 h-3" /> Add interview
                    </button>
                    <button onClick={() => setShowBulk(true)} className="text-xs font-mono uppercase tracking-widest border border-border bg-white px-3 py-2 hover:bg-secondary inline-flex items-center gap-1" data-testid="cal-bulk-slots">
                        <Sparkles className="w-3 h-3" /> Bulk slots
                    </button>
                    <button onClick={() => setShowReminders(true)} className="text-xs font-mono uppercase tracking-widest border border-border px-3 py-2 hover:bg-secondary inline-flex items-center gap-1 relative" data-testid="cal-open-reminders">
                        <Bell className="w-3 h-3" /> My reminders
                    </button>
                    <button onClick={() => onAddNote(null)} className="text-xs font-mono uppercase tracking-widest border border-border px-3 py-2 hover:bg-secondary inline-flex items-center gap-1" data-testid="cal-add-note">
                        <StickyNote className="w-3 h-3" /> Note
                    </button>
                    {viewMode === "month" ? (
                        <>
                            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="border border-border bg-white p-2 hover:bg-secondary" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
                            <div className="font-display font-bold text-lg min-w-[180px] text-center" data-testid="cal-month-label">
                                {cursor.toLocaleString(undefined, { month: "long", year: "numeric" })}
                            </div>
                            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="border border-border bg-white p-2 hover:bg-secondary" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
                            <button onClick={() => { setCursor(startOfMonth(new Date())); setSelectedDate(new Date()); }} className="text-xs font-mono uppercase tracking-widest border border-border px-3 py-2 hover:bg-secondary" data-testid="cal-today">Today</button>
                        </>
                    ) : viewMode === "day" ? (
                        <>
                            <button onClick={() => { const d = new Date(selectedDate); d.setDate(d.getDate() - 1); setSelectedDate(d); }} className="border border-border bg-white p-2 hover:bg-secondary" data-testid="cal-day-prev"><ChevronLeft className="w-4 h-4" /></button>
                            <div className="font-display font-bold text-lg min-w-[220px] text-center" data-testid="cal-day-label">
                                {selectedDate.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
                            </div>
                            <button onClick={() => { const d = new Date(selectedDate); d.setDate(d.getDate() + 1); setSelectedDate(d); }} className="border border-border bg-white p-2 hover:bg-secondary" data-testid="cal-day-next"><ChevronRight className="w-4 h-4" /></button>
                            <button onClick={() => setSelectedDate(new Date())} className="text-xs font-mono uppercase tracking-widest border border-border px-3 py-2 hover:bg-secondary" data-testid="cal-day-today">Today</button>
                        </>
                    ) : (
                        <>
                            <input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} className="h-9 px-2 border border-border bg-white text-sm rounded-none" data-testid="cal-since" />
                            <span className="text-xs text-muted-foreground">→</span>
                            <input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} className="h-9 px-2 border border-border bg-white text-sm rounded-none" data-testid="cal-until" />
                        </>
                    )}
                </div>
            </div>

            {/* Search + filters */}
            <GoogleCalendarBar />

            <div className="bg-white border border-border p-3 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px] relative">
                    <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search interviews, candidates, jobs, interviewers…"
                        className="w-full h-9 pl-9 pr-3 border border-border bg-white text-sm rounded-none"
                        data-testid="cal-search"
                    />
                </div>
                <button onClick={() => setShowFilters(!showFilters)} className={`text-xs font-mono uppercase tracking-widest border border-border px-3 py-2 hover:bg-secondary inline-flex items-center gap-1 ${showFilters ? "bg-secondary" : ""}`} data-testid="cal-filter-toggle">
                    <Filter className="w-3 h-3" /> Filters {anyFilter && <span className="ml-1 bg-ikb text-white px-1.5 rounded">on</span>}
                </button>
                <div className="inline-flex border border-border bg-white" data-testid="cal-scope-toggle">
                    <button onClick={() => setShowAll(false)} className={`px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-widest ${!showAll ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid="cal-scope-mine" title="Only my interviews">Mine</button>
                    <button onClick={() => setShowAll(true)} className={`px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-widest border-l border-border ${showAll ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid="cal-scope-all" title="Everyone in the team">Everyone</button>
                </div>
                {anyFilter && (
                    <button onClick={clearFilters} className="text-xs text-rose-700 hover:underline inline-flex items-center gap-1" data-testid="cal-filter-clear">
                        <X className="w-3 h-3" /> Clear
                    </button>
                )}
                <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">
                    {filteredItems.length} interviews · {filteredNotes.length} notes
                </div>
            </div>

            {/* Recruiter color legend — bubbles toggle visibility per recruiter */}
            {recruiterLegend.length > 0 && (
                <div className="bg-white border border-border px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="cal-recruiter-legend">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 mr-1">Show</span>
                    {recruiterLegend.map((r) => {
                        const col = colorForRecruiter(r.id);
                        const on = isRecruiterOn(r.id);
                        return (
                            <button
                                key={r.id}
                                type="button"
                                onClick={() => toggleRecruiter(r.id)}
                                onDoubleClick={() => onlyRecruiter(r.id)}
                                className={`inline-flex items-center gap-1.5 text-[11px] border px-2 py-1 ${on ? "bg-white border-border" : "bg-secondary/40 border-border opacity-50 line-through"}`}
                                title={on ? "Click to hide · double-click to isolate" : "Click to show"}
                                data-testid={`cal-rec-toggle-${r.id}`}
                            >
                                <span className={`w-2.5 h-2.5 rounded-full ${col.dot}`} />
                                <span className="font-medium text-foreground/80 max-w-[120px] truncate">{r.name}</span>
                            </button>
                        );
                    })}
                    {Object.values(recruiterVisible).some((v) => v === false) && (
                        <button onClick={allRecruitersOn} className="text-[10px] font-mono uppercase tracking-widest text-ikb hover:underline ml-1" data-testid="cal-rec-show-all">
                            Show all
                        </button>
                    )}
                </div>
            )}

            {showFilters && (
                <div className="bg-secondary/30 border border-border p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 text-sm">
                    <select value={fRecruiter} onChange={(e) => setFRecruiter(e.target.value)} className="h-9 px-2 border border-border bg-white" data-testid="cal-f-recruiter">
                        <option value="">All recruiters</option>
                        {recruiters.map((r) => <option key={r.id} value={r.id}>{r.name || r.email}</option>)}
                    </select>
                    <select value={fInterviewer} onChange={(e) => setFInterviewer(e.target.value)} className="h-9 px-2 border border-border bg-white" data-testid="cal-f-interviewer">
                        <option value="">All interviewers</option>
                        {interviewers.map((i) => <option key={i.id} value={i.id}>{i.name || i.email}</option>)}
                    </select>
                    <select value={fJob} onChange={(e) => setFJob(e.target.value)} className="h-9 px-2 border border-border bg-white" data-testid="cal-f-job">
                        <option value="">All jobs</option>
                        {jobsForFilter.map(([jid, title]) => <option key={jid} value={jid}>{title}</option>)}
                    </select>
                    <select value={fCandidate} onChange={(e) => setFCandidate(e.target.value)} className="h-9 px-2 border border-border bg-white" data-testid="cal-f-candidate">
                        <option value="">All candidates</option>
                        {candidatesForFilter.map(([cid, name]) => <option key={cid} value={cid}>{name}</option>)}
                    </select>
                    <select value={fMode} onChange={(e) => setFMode(e.target.value)} className="h-9 px-2 border border-border bg-white" data-testid="cal-f-mode">
                        <option value="">All modes</option>
                        <option value="online">Online</option>
                        <option value="offline">Offline</option>
                    </select>
                    <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="h-9 px-2 border border-border bg-white" data-testid="cal-f-status">
                        <option value="">All statuses</option>
                        <option value="Scheduled">Scheduled</option>
                        <option value="Completed">Completed</option>
                        <option value="Cancelled">Cancelled</option>
                        <option value="No-show">No-show</option>
                    </select>
                </div>
            )}

            <div className="grid lg:grid-cols-12 gap-4">
                <div className="lg:col-span-9 border border-border bg-white">
                    {viewMode === "month" ? (
                        <>
                            <div className="grid grid-cols-7 border-b border-border bg-secondary text-[10px] font-mono uppercase tracking-widest text-foreground/80">
                                {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
                                    <div key={d} className="px-2 py-2 text-center">{d}</div>
                                ))}
                            </div>
                            <div className="grid grid-cols-7">
                                {weeks.flat().map((d) => {
                                    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                                    const events = eventsByDay.get(key) || [];
                                    const dayNotes = notesByDay.get(key) || [];
                                    const outside = d.getMonth() !== cursor.getMonth();
                                    const isToday = isSameDay(d, today);
                                    const isPast = d < new Date(today.getFullYear(), today.getMonth(), today.getDate());
                                    return (
                                        <div
                                            key={key}
                                            data-testid={`cal-day-${key}`}
                                            onClick={() => onDayClick(d)}
                                            className={`group min-h-[110px] p-2 border-b border-r border-border relative cursor-pointer ${outside ? "bg-secondary/30" : "bg-white"} ${isToday ? "ring-2 ring-ikb ring-inset" : isSameDay(d, selectedDate) ? "ring-2 ring-amber-500 ring-inset" : ""}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className={`text-xs font-mono ${isToday ? "text-ikb font-bold" : outside ? "text-foreground/40" : "text-foreground"}`}>
                                                    {d.getDate()}
                                                </div>
                                                {!isPast && (
                                                    <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); addInterview(d); }}
                                                            title="Block an interview slot"
                                                            className="text-[10px] bg-ikb text-white px-1 py-0.5 inline-flex items-center gap-0.5"
                                                            data-testid={`cal-create-slot-${key}`}
                                                        >
                                                            <Plus className="w-2.5 h-2.5" />
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); onAddNote(d); }}
                                                            title="Add a note / reminder"
                                                            className="text-[10px] bg-amber-500 text-white px-1 py-0.5 inline-flex items-center gap-0.5"
                                                            data-testid={`cal-create-note-${key}`}
                                                        >
                                                            <StickyNote className="w-2.5 h-2.5" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="space-y-1 mt-1">
                                                {events.slice(0, 3).map((e) => {
                                                    const interviewers = (e.interviewer_names || []).slice(0, 3).join(", ");
                                                    const ttl = [
                                                        e.round_name || "Interview",
                                                        e.candidate?.name || "Unattached",
                                                        e.job?.title || "",
                                                        interviewers ? `with ${interviewers}` : "",
                                                        e.created_by_name ? `by ${e.created_by_name}` : "",
                                                    ].filter(Boolean).join(" · ");
                                                    const isMine = !user?.id || e.created_by === user.id;
                                                    const col = e.is_placeholder || !e.candidate_id ? { bg: "bg-amber-50", border: "border-amber-500", dot: "bg-amber-500" } : colorForRecruiter(e.created_by);
                                                    const dim = !isMine && showAll;
                                                    return (
                                                    <div
                                                        key={e.id}
                                                        className={`group relative w-full ${col.bg} ${col.border} border-l-2 ${dim ? "opacity-70" : ""}`}
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={(ev) => { ev.stopPropagation(); setSelected(e); }}
                                                            className="w-full text-left block text-[10px] px-1.5 py-1 pr-6 truncate hover:opacity-90"
                                                            data-testid={`cal-event-${e.id}`}
                                                            title={ttl}
                                                        >
                                                            {e.status === "Completed" && <span className="text-emerald-700 font-bold mr-0.5" title="Completed">✓</span>}
                                                            <span className="font-mono font-bold">{fmtTime(e.scheduled_at)}</span>
                                                            <span className="ml-1">{e.candidate?.name || "Unattached"}</span>
                                                            {e.job?.title && <span className="ml-1 text-foreground/60">· {e.job.title}</span>}
                                                            {(e.interviewer_feedbacks || []).length > 0 && (
                                                                <span className="ml-1 text-[9px] font-mono text-emerald-700" title={`${(e.interviewer_feedbacks || []).length} interviewer assessment(s)`}>
                                                                    · {(e.interviewer_feedbacks || []).length}★
                                                                </span>
                                                            )}
                                                        </button>
                                                        {/* Inline resend — hover-reveal so the card stays uncluttered on dense months */}
                                                        <div className="absolute right-0.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <ResendInviteIconBtn
                                                                interview={e}
                                                                onSent={(resp) => {
                                                                    // Mirror the new invite_sent_at into the list so the hover
                                                                    // tooltip reflects "Last sent now" without a reload.
                                                                    const stamp = resp?.sent_at || new Date().toISOString();
                                                                    setItems((prev) => prev.map((row) =>
                                                                        row.id === e.id ? { ...row, invite_sent_at: stamp } : row
                                                                    ));
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                    );
                                                })}
                                                {events.length > 3 && (
                                                    <div className="text-[10px] text-foreground/80 font-mono">+{events.length - 3} more</div>
                                                )}
                                                {dayNotes.slice(0, 2).map((n) => (
                                                    <button
                                                        key={n.id}
                                                        type="button"
                                                        onClick={(ev) => { ev.stopPropagation(); setNoteSelected(n); }}
                                                        className={`w-full text-left block text-[10px] px-1.5 py-1 truncate border-l-2 bg-${n.color === "rose" ? "rose" : n.color === "amber" ? "amber" : n.color === "emerald" ? "emerald" : n.color === "grey" ? "slate" : "blue"}-50 border-${n.color === "rose" ? "rose" : n.color === "amber" ? "amber" : n.color === "emerald" ? "emerald" : n.color === "grey" ? "slate" : "ikb"}-500 hover:opacity-80`}
                                                        data-testid={`cal-note-${n.id}`}
                                                    >
                                                        <span className="inline-flex items-center gap-1">
                                                            <span className={`w-1.5 h-1.5 rounded-full ${COLOR_DOT[n.color] || COLOR_DOT.blue}`} />
                                                            <span className="font-mono">{n.time || "Note"}</span> · {n.title}
                                                        </span>
                                                    </button>
                                                ))}
                                                {dayNotes.length > 2 && (
                                                    <div className="text-[10px] text-foreground/80 font-mono">+{dayNotes.length - 2} notes</div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    ) : viewMode === "list" ? (
                        <div className="divide-y divide-border" data-testid="cal-list-view">
                            {loading ? (
                                <div className="p-12 text-center text-xs font-mono text-muted-foreground">Loading…</div>
                            ) : filteredItems.length === 0 && filteredNotes.length === 0 ? (
                                <div className="p-12 text-center text-xs font-mono text-muted-foreground">Nothing matches the filters.</div>
                            ) : (
                                <>
                                    {filteredItems.map((e) => (
                                        <SmartInterviewRow key={e.id} e={e} onOpen={setSelected} onPreviewCandidate={setPreviewCid} />
                                    ))}
                                    {filteredNotes.map((n) => (
                                        <button
                                            key={n.id}
                                            type="button"
                                            onClick={() => setNoteSelected(n)}
                                            className="w-full text-left p-3 sm:p-4 hover:bg-secondary/50 flex items-center gap-4 flex-wrap"
                                            data-testid={`cal-list-note-${n.id}`}
                                        >
                                            <div className="text-xs font-mono uppercase tracking-widest text-amber-700 min-w-[150px]">
                                                {n.date}{n.time ? ` · ${n.time}` : ""}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-sm inline-flex items-center gap-2">
                                                    <span className={`w-2 h-2 rounded-full ${COLOR_DOT[n.color] || COLOR_DOT.blue}`} />
                                                    {n.title}
                                                </div>
                                                {n.body && <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.body}</div>}
                                            </div>
                                            <span className="text-[10px] font-mono uppercase tracking-widest border border-amber-300 bg-amber-50 text-amber-800 px-1.5 py-0.5 inline-flex items-center gap-1">
                                                <StickyNote className="w-3 h-3" /> NOTE
                                            </span>
                                        </button>
                                    ))}
                                </>
                            )}
                        </div>
                    ) : viewMode === "schedule" ? (
                        <ScheduleView filteredItems={filteredItems} filteredNotes={filteredNotes} loading={loading} onOpen={setSelected} onPreviewCandidate={setPreviewCid} onOpenNote={setNoteSelected} />
                    ) : viewMode === "day" ? (
                        <DayView
                            date={selectedDate}
                            items={filteredItems}
                            notes={filteredNotes}
                            loading={loading}
                            onOpen={setSelected}
                            onPreviewCandidate={setPreviewCid}
                            onOpenNote={setNoteSelected}
                            onCreateSlotAt={(d) => addInterview(d)}
                            colorForRecruiter={colorForRecruiter}
                            currentUserId={user?.id}
                            showAll={showAll}
                            onToggleComplete={async (n) => {
                                try {
                                    await api.post(`/calendar/notes/${n.id}/toggle-complete`);
                                    load();
                                } catch (err) { toast.error(formatApiError(err)); }
                            }}
                            onMove={async (evnt, newDate) => {
                                try {
                                    await api.patch(`/calendar/interviews/${evnt.id}`, { scheduled_at: newDate.toISOString() });
                                    toast.success("Moved to " + newDate.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }));
                                    load();
                                } catch (err) { toast.error(formatApiError(err)); }
                            }}
                            onResize={async (evnt, newDur) => {
                                try {
                                    await api.patch(`/calendar/interviews/${evnt.id}`, { duration_minutes: newDur });
                                    toast.success(`Duration → ${newDur}m`);
                                    load();
                                } catch (err) { toast.error(formatApiError(err)); }
                            }}
                            onInviteSent={(evntId, resp) => {
                                const stamp = resp?.sent_at || new Date().toISOString();
                                setItems((prev) => prev.map((row) =>
                                    row.id === evntId ? { ...row, invite_sent_at: stamp } : row
                                ));
                            }}
                        />
                    ) : null}
                </div>

                <aside className="lg:col-span-3 space-y-3" data-testid="cal-side-panel">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <h2 className="font-display font-bold text-sm uppercase tracking-widest text-foreground/80">
                            {selectedDate.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
                        </h2>
                        <div className="flex items-center gap-1">
                            <button onClick={() => addInterview(selectedDate)} className="text-[10px] font-mono uppercase tracking-widest border border-ikb bg-ikb text-white px-2 py-1 inline-flex items-center gap-1" data-testid="side-add-interview">
                                <Plus className="w-3 h-3" /> Add
                            </button>
                            <button onClick={() => onAddNote(selectedDate)} className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-1 inline-flex items-center gap-1" data-testid="side-add-note">
                                <StickyNote className="w-3 h-3" /> Note
                            </button>
                        </div>
                    </div>
                    {(() => {
                        const dayKey = `${selectedDate.getFullYear()}-${selectedDate.getMonth()}-${selectedDate.getDate()}`;
                        const dayEvents = (eventsByDay.get(dayKey) || []).sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
                        const dayN = (notesByDay.get(dayKey) || []).sort((a, b) => (a.time || "").localeCompare(b.time || ""));
                        if (loading) return <div className="text-xs font-mono text-foreground/80">Loading…</div>;
                        if (dayEvents.length === 0 && dayN.length === 0) {
                            return (
                                <div className="border border-dashed border-border bg-white p-4 text-xs text-foreground/80">
                                    Nothing scheduled. Click <span className="font-mono">Add</span> to block a slot or <span className="font-mono">Note</span> to add a reminder.
                                </div>
                            );
                        }
                        return (
                            <>
                                {dayEvents.map((e) => (
                                    <SmartInterviewRow key={e.id} e={e} onOpen={setSelected} onPreviewCandidate={setPreviewCid} variant="card" />
                                ))}
                                {dayN.map((n) => (
                                    <button key={n.id} type="button" onClick={() => setNoteSelected(n)} className="w-full text-left border border-border bg-white p-3 hover:bg-secondary/40" data-testid={`side-note-${n.id}`}>
                                        <div className="flex items-center gap-2">
                                            <span className={`w-2 h-2 rounded-full ${COLOR_DOT[n.color] || COLOR_DOT.blue}`} />
                                            <span className="text-[10px] font-mono uppercase tracking-widest text-amber-700">{n.time || "NOTE"}</span>
                                            <StickyNote className="w-3 h-3 text-amber-500" />
                                        </div>
                                        <div className="text-sm font-medium mt-1">{n.title}</div>
                                        {n.body && <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.body}</div>}
                                    </button>
                                ))}
                            </>
                        );
                    })()}

                    {/* Upcoming roll-up */}
                    <div className="pt-3 border-t border-border">
                        <h3 className="font-display font-bold text-xs uppercase tracking-widest text-foreground/60 mb-2">Next up</h3>
                        {upcoming.slice(0, 4).map((e) => (
                            <button type="button" key={e.id} onClick={() => { setSelected(e); }} className="w-full text-left block border border-border bg-white p-2 mb-1.5 hover:bg-secondary" data-testid={`upcoming-${e.id}`}>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-ikb">{new Date(e.scheduled_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · {fmtTime(e.scheduled_at)}</div>
                                <div className="text-xs font-medium mt-0.5 truncate">{e.candidate?.name || "Unattached"}</div>
                                <div className="text-[10px] text-foreground/70 truncate">{e.job?.title || "—"}</div>
                            </button>
                        ))}
                        {upcoming.length === 0 && <div className="text-[11px] font-mono text-foreground/60">Nothing else this week.</div>}
                    </div>
                </aside>
            </div>

            <BulkSlotsDialog open={showBulk} onClose={() => setShowBulk(false)} onCreated={load} />

            <RemindersDialog
                open={showReminders}
                onOpenChange={setShowReminders}
                onOpen={(n) => setNoteSelected(n)}
                refreshKey={notes.length}
                currentUserId={user?.id}
            />

            <InterviewDetailDialog
                open={!!selected}
                interview={selected}
                companyAddress={companyAddress}
                onClose={() => setSelected(null)}
                onSaved={load}
            />
            <CalendarNoteDialog
                open={!!noteSelected}
                note={noteSelected}
                onClose={() => setNoteSelected(null)}
                onSaved={load}
            />
            {previewCid && (
                <CandidateProfileDialog
                    open={!!previewCid}
                    candidateId={previewCid}
                    onClose={() => setPreviewCid(null)}
                />
            )}
        </div>
    );
}


/** Schedule view — chronological list grouped by date (like Google Calendar's "Schedule"). */
function ScheduleView({ filteredItems, filteredNotes, loading, onOpen, onPreviewCandidate, onOpenNote }) {
    if (loading) return <div className="p-12 text-center text-xs font-mono text-muted-foreground">Loading…</div>;
    const groups = new Map();
    for (const it of filteredItems) {
        if (!it.scheduled_at) continue;
        const d = new Date(it.scheduled_at);
        const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        if (!groups.has(k)) groups.set(k, { date: d, items: [], notes: [] });
        groups.get(k).items.push(it);
    }
    for (const n of filteredNotes) {
        if (!n.date) continue;
        if (!groups.has(n.date)) {
            const [y, m, dd] = n.date.split("-").map(Number);
            groups.set(n.date, { date: new Date(y, m - 1, dd), items: [], notes: [] });
        }
        groups.get(n.date).notes.push(n);
    }
    const ordered = [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    if (ordered.length === 0) {
        return <div className="p-12 text-center text-xs font-mono text-muted-foreground" data-testid="cal-schedule-empty">Nothing in this range.</div>;
    }
    const todayKey = (() => { const t = new Date(); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`; })();
    return (
        <div data-testid="cal-schedule-view">
            {ordered.map(([k, g]) => (
                <div key={k} className="border-b border-border" data-testid={`cal-schedule-group-${k}`}>
                    <div className={`px-4 py-2 text-[11px] font-mono uppercase tracking-widest sticky top-0 ${k === todayKey ? "bg-ikb text-white" : "bg-secondary text-foreground/70"}`}>
                        {g.date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
                        {k === todayKey && <span className="ml-2 text-[10px] bg-white text-ikb px-1.5 py-0.5">Today</span>}
                    </div>
                    {g.items.length === 0 && g.notes.length === 0 ? (
                        <div className="p-4 text-xs text-foreground/50 font-mono">No items</div>
                    ) : (
                        <>
                            {g.items.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)).map((e) => (
                                <SmartInterviewRow key={e.id} e={e} onOpen={onOpen} onPreviewCandidate={onPreviewCandidate} />
                            ))}
                            {g.notes.sort((a, b) => (a.time || "").localeCompare(b.time || "")).map((n) => (
                                <button key={n.id} type="button" onClick={() => onOpenNote(n)} className="w-full text-left p-3 sm:p-4 hover:bg-secondary/40 flex items-center gap-4 border-t border-border" data-testid={`cal-schedule-note-${n.id}`}>
                                    <div className="text-xs font-mono uppercase tracking-widest text-amber-700 min-w-[150px]">{n.time || "Reminder"}</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium">{n.title}</div>
                                        {n.body && <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{n.body}</div>}
                                    </div>
                                </button>
                            ))}
                        </>
                    )}
                </div>
            ))}
        </div>
    );
}


/** Day view — 15-min interval timeline. Click empty slot to create.
 * Drag an existing event vertically to move it (snap to 15-min). Drag the
 * bottom resize handle to extend/shrink its duration.
 *
 * Notes/reminders without time appear in an "All-day" strip at the top.
 * Notes with a time render as an inline marker on the right rail of the
 * timeline at that exact slot. */
function DayView({ date, items, notes, loading, onOpen, onPreviewCandidate, onOpenNote, onCreateSlotAt, onMove, onResize, onToggleComplete, colorForRecruiter, currentUserId, showAll, onInviteSent }) {
    const dayStart = new Date(date); dayStart.setHours(8, 0, 0, 0);
    const dayEnd = new Date(date); dayEnd.setHours(21, 0, 0, 0);
    const SLOT_HEIGHT = 14; // px per 15 min
    const slotsCount = ((dayEnd - dayStart) / 60000) / 15;

    const sameDay = (a) => {
        const d = new Date(a);
        return d.getFullYear() === date.getFullYear() && d.getMonth() === date.getMonth() && d.getDate() === date.getDate();
    };
    const dayItems = items.filter((it) => it.scheduled_at && sameDay(it.scheduled_at));
    const dayNotes = notes.filter((n) => {
        if (!n.date) return false;
        const [y, m, dd] = n.date.split("-").map(Number);
        return y === date.getFullYear() && m - 1 === date.getMonth() && dd === date.getDate();
    });
    // Split day-level (no time) vs timed reminders
    const allDayNotes = dayNotes.filter((n) => !n.time);
    const timedNotes = dayNotes.filter((n) => !!n.time);

    const minutesFromTop = (iso) => {
        const t = new Date(iso);
        return (t - dayStart) / 60000;
    };
    const noteTopFromTime = (timeStr) => {
        // "HH:MM" -> minutes from dayStart -> px
        if (!timeStr) return null;
        const [hh, mm] = timeStr.split(":").map((n) => parseInt(n, 10));
        if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
        const minsFromStart = (hh - dayStart.getHours()) * 60 + (mm - dayStart.getMinutes());
        return Math.max(0, Math.min(minsFromStart / 15 * SLOT_HEIGHT, slotsCount * SLOT_HEIGHT - SLOT_HEIGHT));
    };

    // Drag state: { id, kind: "move"|"resize", startY, startMinTop, startDur }
    const dragRef = useRef(null);
    const gridRef = useRef(null);
    const [dragPreview, setDragPreview] = useState(null);   // { id, top, height }

    const snap = (px) => Math.round(px / SLOT_HEIGHT) * SLOT_HEIGHT;
    const minutesAt = (px) => (px / SLOT_HEIGHT) * 15;

    const onMouseDown = (ev, evnt, kind) => {
        ev.preventDefault();
        const top0 = minutesFromTop(evnt.scheduled_at) / 15 * SLOT_HEIGHT;
        const dur0 = evnt.duration_minutes || 30;
        dragRef.current = { evnt, kind, startY: ev.clientY, startTop: top0, startDurPx: (dur0 / 15) * SLOT_HEIGHT };
        setDragPreview({ id: evnt.id, top: top0, height: (dur0 / 15) * SLOT_HEIGHT });
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    };
    const onMouseMove = (ev) => {
        const st = dragRef.current; if (!st) return;
        const delta = ev.clientY - st.startY;
        if (st.kind === "move") {
            const top = Math.max(0, Math.min(snap(st.startTop + delta), slotsCount * SLOT_HEIGHT - st.startDurPx));
            setDragPreview({ id: st.evnt.id, top, height: st.startDurPx });
        } else {
            const height = Math.max(SLOT_HEIGHT, Math.min(snap(st.startDurPx + delta), slotsCount * SLOT_HEIGHT - st.startTop));
            setDragPreview({ id: st.evnt.id, top: st.startTop, height });
        }
    };
    const onMouseUp = () => {
        const st = dragRef.current; const dp = dragPreview;
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        dragRef.current = null;
        if (!st || !dp) { setDragPreview(null); return; }
        if (st.kind === "move") {
            const newMin = minutesAt(dp.top);
            const newDate = new Date(dayStart.getTime() + newMin * 60000);
            if (Math.abs(newMin - minutesFromTop(st.evnt.scheduled_at)) >= 15) onMove?.(st.evnt, newDate);
        } else {
            const newDur = Math.round(minutesAt(dp.height));
            if (newDur && newDur !== st.evnt.duration_minutes) onResize?.(st.evnt, newDur);
        }
        setDragPreview(null);
    };

    const noteColorClass = (c) => {
        const m = { grey: "border-slate-400 bg-slate-50 text-slate-700",
                    blue: "border-ikb bg-blue-50 text-ikb",
                    amber: "border-amber-500 bg-amber-50 text-amber-700",
                    rose: "border-rose-500 bg-rose-50 text-rose-700",
                    emerald: "border-emerald-500 bg-emerald-50 text-emerald-700" };
        return m[c || "blue"] || m.blue;
    };

    /**
     * Lay out overlapping events into parallel columns (Google Calendar style).
     * Algorithm: greedy lane assignment per cluster of overlapping items.
     * Returns: [{ event, lane, lanes }] where `lanes` = total parallel columns
     * in this event's cluster, `lane` = 0..lanes-1.
     */
    const dayLayout = useMemo(() => {
        const sorted = [...dayItems]
            .filter((e) => e.scheduled_at)
            .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
        const start = (e) => new Date(e.scheduled_at).getTime();
        const end = (e) => start(e) + ((e.duration_minutes || 30) * 60000);
        const out = [];
        let cluster = [];
        let clusterEnd = 0;
        const flush = () => {
            if (!cluster.length) return;
            // Assign each event the lowest lane index whose lane is free at its start
            const lanes = []; // lanes[i] = end-time of last event placed in lane i
            const placed = [];
            for (const e of cluster) {
                let li = lanes.findIndex((endT) => endT <= start(e));
                if (li === -1) { li = lanes.length; lanes.push(0); }
                lanes[li] = end(e);
                placed.push({ event: e, lane: li });
            }
            const total = lanes.length;
            for (const p of placed) out.push({ ...p, lanes: total });
            cluster = []; clusterEnd = 0;
        };
        for (const e of sorted) {
            if (cluster.length && start(e) >= clusterEnd) flush();
            cluster.push(e);
            clusterEnd = Math.max(clusterEnd, end(e));
        }
        flush();
        return out;
    }, [dayItems]);

    return (
        <div className="p-3" data-testid="cal-day-view">
            {loading && <div className="p-6 text-center text-xs font-mono text-muted-foreground">Loading…</div>}

            {/* All-day reminders strip — notes without a time */}
            {allDayNotes.length > 0 && (
                <div className="mb-3 border border-amber-200 bg-amber-50/50 p-2" data-testid="cal-day-allday">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 mb-1.5 flex items-center gap-1.5">
                        <StickyNote className="w-3 h-3" /> All-day reminders ({allDayNotes.length})
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {allDayNotes.map((n) => (
                            <button
                                key={n.id}
                                type="button"
                                onClick={() => onOpenNote(n)}
                                className={`inline-flex items-center gap-1.5 border-l-2 px-2 py-1 text-xs hover:bg-white ${noteColorClass(n.color)} ${n.completed ? "opacity-50 line-through" : ""}`}
                                data-testid={`cal-day-allday-note-${n.id}`}
                                title={n.body || n.title}
                            >
                                {onToggleComplete && (
                                    <input
                                        type="checkbox"
                                        checked={!!n.completed}
                                        onChange={(e) => { e.stopPropagation(); onToggleComplete(n); }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-3 h-3 accent-ikb"
                                        data-testid={`cal-day-allday-check-${n.id}`}
                                    />
                                )}
                                <span className="font-medium truncate max-w-[200px]">{n.title}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="overflow-x-auto">
            <div className="grid grid-cols-[60px_1fr_140px] relative min-w-[520px]" style={{ height: slotsCount * SLOT_HEIGHT + 24 }} ref={gridRef}>
                {/* Hour labels */}
                <div className="border-r border-border">
                    {Array.from({ length: slotsCount + 1 }).map((_, i) => {
                        const t = new Date(dayStart.getTime() + i * 15 * 60000);
                        const showLabel = t.getMinutes() === 0;
                        return (
                            <div key={i} style={{ height: SLOT_HEIGHT }} className={`relative ${showLabel ? "border-t border-border" : ""}`}>
                                {showLabel && <span className="absolute -top-2 right-1 text-[9px] font-mono text-foreground/50">{t.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>}
                            </div>
                        );
                    })}
                </div>
                {/* Slots */}
                <div className="relative border-r border-border">
                    {Array.from({ length: slotsCount }).map((_, i) => {
                        const t = new Date(dayStart.getTime() + i * 15 * 60000);
                        const showLabel = t.getMinutes() === 0;
                        return (
                            <button
                                key={i}
                                type="button"
                                onClick={() => onCreateSlotAt?.(t)}
                                style={{ height: SLOT_HEIGHT }}
                                className={`w-full ${showLabel ? "border-t border-border" : "border-t border-border/30"} hover:bg-secondary/50`}
                                data-testid={`cal-day-slot-${i}`}
                                title={`Create interview at ${t.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`}
                            />
                        );
                    })}
                    {/* Bottom-of-day cap line */}
                    <div className="absolute left-0 right-0 bottom-0 border-b-2 border-border" />
                    {dayLayout.map(({ event: e, lane, lanes }) => {
                        const isPreview = dragPreview && dragPreview.id === e.id;
                        const baseTop = minutesFromTop(e.scheduled_at) / 15 * SLOT_HEIGHT;
                        const baseHeight = Math.max(SLOT_HEIGHT, ((e.duration_minutes || 30) / 15) * SLOT_HEIGHT - 2);
                        const top = isPreview ? dragPreview.top : baseTop;
                        const height = isPreview ? dragPreview.height : baseHeight;
                        if (top < 0) return null;
                        // Lane positioning — percent-based for fluid resize
                        const widthPct = lanes > 1 ? (100 / lanes) : 100;
                        const leftPct = lane * widthPct;
                        const isMine = !currentUserId || e.created_by === currentUserId;
                        const col = e.is_placeholder || !e.candidate_id ? { bg: "bg-amber-50", border: "border-amber-500" } : colorForRecruiter ? colorForRecruiter(e.created_by) : { bg: "bg-blue-50", border: "border-ikb" };
                        const dim = !isMine && showAll;
                        return (
                            <div
                                key={e.id}
                                style={{ top, height, left: `calc(${leftPct}% + 2px)`, width: `calc(${widthPct}% - 4px)` }}
                                className={`absolute select-none ${col.bg} border-l-2 ${col.border} ${dim ? "opacity-70" : ""} overflow-hidden shadow-sm group`}
                                data-testid={`cal-day-event-${e.id}`}
                                data-lane={lane}
                                data-lanes={lanes}
                            >
                                {/* Move handle = whole body (top area) */}
                                <button
                                    type="button"
                                    onMouseDown={(ev) => onMouseDown(ev, e, "move")}
                                    onClick={() => onOpen?.(e)}
                                    className="w-full text-left px-2 py-1 cursor-move"
                                    style={{ height: Math.max(SLOT_HEIGHT, height - 6) }}
                                    title="Drag to move · click to open"
                                >
                                    <div className="text-[10px] font-mono font-bold truncate">{new Date(e.scheduled_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} · {isPreview ? Math.round((dragPreview.height / SLOT_HEIGHT) * 15) : (e.duration_minutes || 30)}m</div>
                                    <div className="text-xs font-medium truncate">{e.candidate?.name || "Unattached"}</div>
                                    {e.job?.title && lanes < 3 && <div className="text-[10px] text-foreground/70 truncate">{e.job.title}</div>}
                                    {e.created_by_name && showAll && lanes < 4 && <div className="text-[9px] font-mono text-foreground/50 truncate">by {e.created_by_name}</div>}
                                </button>
                                {/* Resize handle */}
                                <div
                                    onMouseDown={(ev) => onMouseDown(ev, e, "resize")}
                                    className="absolute left-0 right-0 bottom-0 h-1.5 bg-foreground/10 cursor-ns-resize opacity-0 group-hover:opacity-100"
                                    title="Drag to resize"
                                    data-testid={`cal-day-resize-${e.id}`}
                                />
                                {/* Phase 158g — hover-reveal Resend invite icon pinned top-right */}
                                <div className="absolute right-1 top-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <ResendInviteIconBtn interview={e} onSent={(resp) => onInviteSent?.(e.id, resp)} />
                                </div>
                            </div>
                        );
                    })}
                </div>
                {/* Right rail — timed reminders pinned at their slot */}
                <div className="relative">
                    {timedNotes.map((n) => {
                        const top = noteTopFromTime(n.time);
                        if (top == null) return null;
                        return (
                            <button
                                key={n.id}
                                type="button"
                                onClick={() => onOpenNote(n)}
                                style={{ top, left: 4, right: 4 }}
                                className={`absolute border-l-2 ${noteColorClass(n.color)} px-1.5 py-1 text-left hover:brightness-95 ${n.completed ? "opacity-50 line-through" : ""}`}
                                data-testid={`cal-day-timed-note-${n.id}`}
                                title={n.body || n.title}
                            >
                                <div className="flex items-center gap-1">
                                    {onToggleComplete && (
                                        <input
                                            type="checkbox"
                                            checked={!!n.completed}
                                            onChange={(e) => { e.stopPropagation(); onToggleComplete(n); }}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-3 h-3 accent-ikb shrink-0"
                                            data-testid={`cal-day-timed-check-${n.id}`}
                                        />
                                    )}
                                    <StickyNote className="w-2.5 h-2.5 shrink-0" />
                                    <span className="text-[9px] font-mono">{n.time}</span>
                                </div>
                                <div className="text-[11px] font-medium truncate mt-0.5">{n.title}</div>
                            </button>
                        );
                    })}
                    {timedNotes.length === 0 && (
                        <div className="text-[9px] font-mono text-foreground/40 px-1 py-1">No timed reminders</div>
                    )}
                </div>
            </div>
            </div>
        </div>
    );
}



/** Mini bar showing Google Calendar connection + sync status. Mocked today. */
function GoogleCalendarBar() {
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = async () => {
        try {
            const { data } = await api.get("/calendar/google/status");
            setStatus(data);
        } catch { setStatus({ connected: false }); }
    };
    useEffect(() => { load(); }, []);
    const connect = async () => {
        setBusy(true);
        try { await api.post("/calendar/google/connect", {}); toast.success("Connected (mock)"); load(); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    const sync = async () => {
        setBusy(true);
        try { const { data } = await api.post("/calendar/google/sync"); toast.success(`Imported ${data.imported} events`); load(); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    const disconnect = async () => {
        if (!window.confirm("Disconnect Google Calendar and remove imported events?")) return;
        setBusy(true);
        try { await api.post("/calendar/google/disconnect"); toast.success("Disconnected"); load(); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    if (!status) return null;
    return (
        <div className="border border-border bg-white px-3 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="gcal-bar">
            <div className="flex items-center gap-2 text-xs">
                <span className="text-[10px] font-mono uppercase tracking-widest border border-amber-300 bg-amber-50 text-amber-700 px-1.5 py-0.5">MOCK</span>
                <span className="font-mono uppercase tracking-widest text-[10px] text-foreground/60">Google Calendar</span>
                {status.connected ? (
                    <>
                        <span className="text-emerald-700 text-[11px] font-mono">● {status.email}</span>
                        {status.last_sync_at && <span className="text-[10px] font-mono text-foreground/50">last sync {new Date(status.last_sync_at).toLocaleString()}</span>}
                    </>
                ) : (
                    <span className="text-[11px] font-mono text-foreground/60">Not connected</span>
                )}
            </div>
            <div className="flex items-center gap-1.5">
                {!status.connected ? (
                    <button type="button" onClick={connect} disabled={busy} className="text-xs font-mono uppercase tracking-widest border border-ikb bg-ikb text-white px-3 py-1.5 hover:bg-[#001f7a] disabled:opacity-50" data-testid="gcal-connect">
                        {busy ? "…" : "Connect"}
                    </button>
                ) : (
                    <>
                        <button type="button" onClick={sync} disabled={busy} className="text-xs font-mono uppercase tracking-widest border border-ikb bg-ikb text-white px-3 py-1.5 hover:bg-[#001f7a] disabled:opacity-50" data-testid="gcal-sync">
                            {busy ? "Syncing…" : "Sync now"}
                        </button>
                        <button type="button" onClick={disconnect} disabled={busy} className="text-xs font-mono uppercase tracking-widest border border-border bg-white px-3 py-1.5 hover:bg-secondary disabled:opacity-50" data-testid="gcal-disconnect">
                            Disconnect
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
